import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { CATEGORIES, labelFor, IconFor } from "@/lib/categories";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { FalseDeclarationWarning } from "@/components/FalseDeclarationWarning";
import { useB2C } from "@/context/B2CAuthContext";
import { Search, PackageSearch, MapPin, Hand, BellRing, CheckCircle2 } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { FindoSpinner } from "@/components/FindoSpinner";

function NotifyMe({ partnerId, q, category }) {
  const { t } = useLang();
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  async function submit(e) {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await api(`/public/lookup/${partnerId}/watch`, {
        method: "POST", auth: false,
        body: { email: email.trim(), query: q || undefined, category: category || undefined },
      });
      setDone(true);
    } catch { /* keep silent, show generic */ setDone(true); }
    finally { setBusy(false); }
  }
  if (done) {
    return (
      <div className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-emerald-700" data-testid="notify-done">
        <CheckCircle2 size={16} /> {t("pub.notifyDone")}
      </div>
    );
  }
  return (
    <form onSubmit={submit} className="mt-3 flex flex-wrap items-center justify-center gap-2" data-testid="notify-form">
      <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
        placeholder={t("pub.yourEmailShort")} className="h-11 rounded-xl max-w-xs" data-testid="notify-email" />
      <Button type="submit" disabled={busy} className="rounded-xl font-semibold" data-testid="notify-submit">
        <BellRing size={16} className="mr-1.5" /> {t("pub.alertMeOnArrival")}
      </Button>
    </form>
  );
}

export default function LibreService() {
  const { t } = useLang();
  const { partnerId } = useParams();
  const [sp] = useSearchParams();
  const est = sp.get("e") || "";
  const [partner, setPartner] = useState(null);
  const [items, setItems] = useState(null);
  const [catCounts, setCatCounts] = useState({});
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [loading, setLoading] = useState(false);
  const [claimItem, setClaimItem] = useState(null);
  const { user } = useB2C();
  const navigate = useNavigate();

  // After the legal warning: logged-in → straight to the form; otherwise login first, then back to the form.
  function continueClaim() {
    const item = claimItem;
    setClaimItem(null);
    const formPath = `/declare/${partnerId}${est ? `?e=${est}` : ""}`;
    if (!user) {
      navigate(`/mon-compte?next=${encodeURIComponent(formPath)}`);
      return;
    }
    if (user.email && item?.id) {
      api(`/public/lookup/${partnerId}/claim-intent`, {
        method: "POST", auth: false, body: { email: user.email, item_id: item.id },
      }).catch(() => {});
    }
    navigate(formPath);
  }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (category) params.set("category", category);
      if (est) params.set("e", est);
      const res = await api(`/public/lookup/${partnerId}?${params.toString()}`, { auth: false });
      setPartner(res.partner);
      setItems(res.items || []);
      setCatCounts(res.category_counts || {});
    } catch {
      setItems([]);
    } finally { setLoading(false); }
  }, [partnerId, q, category, est]);

  useEffect(() => {
    const id = setTimeout(load, 250);
    return () => clearTimeout(id);
  }, [load]);

  return (
    <div className="min-h-[70vh]" data-testid="libre-service-page">
      {/* Hero */}
      <div className="findo-gradient text-white">
        <div className="max-w-5xl mx-auto px-6 py-12 md:py-16">
          <p className="text-sm font-semibold uppercase tracking-wide opacity-80">{t("pub.foundSelfService")}</p>
          <h1 className="mt-2 font-display font-bold text-3xl md:text-4xl">
            {partner?.org_name ? partner.org_name : t("pub.lsSearchTitle")}
          </h1>
          <p className="mt-2 text-white/85 max-w-2xl">
            {t("pub.lsHeroDesc")}
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 -mt-8">
        <div className="bg-white rounded-2xl border border-border card-shadow p-4 flex flex-wrap items-center gap-3" data-testid="ls-toolbar">
          <div className="relative flex-1 min-w-56">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("pub.describeItemPlaceholder")}
              className="pl-10 h-11 rounded-xl" data-testid="ls-search" />
          </div>
          <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="ls-category"
            className="h-11 rounded-xl border border-border bg-white px-3 text-sm text-slate-600 max-w-[220px]">
            <option value="">{t("pub.allCategories")}</option>
            {[...CATEGORIES].sort((a, b) => a.label.localeCompare(b.label)).map((c) => (
              <option key={c.key} value={c.key}>{c.label}{catCounts[c.key] ? ` (${catCounts[c.key]})` : ""}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-slate-500" data-testid="ls-count">
            {items === null ? <span className="inline-flex items-center gap-1.5"><FindoSpinner size={16} />{t("c.loading")}</span> : t("pub.itemsAvailableCount").replace("{n}", items.length)}
          </p>
          {loading && <FindoSpinner size={16} />}
        </div>

        {items === null ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => <div key={i} className="h-60 rounded-2xl bg-slate-100 animate-pulse" />)}
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-20 border-2 border-dashed border-border rounded-2xl" data-testid="ls-empty">
            <div className="mx-auto h-16 w-16 grid place-items-center rounded-2xl bg-findo-indigo/10 text-findo-indigo"><PackageSearch size={32} /></div>
            <h3 className="mt-4 font-display font-bold text-lg text-slate-900">{t("pub.noMatchingItem")}</h3>
            <p className="mt-1 text-slate-500 text-sm">{t("pub.noItemMatch")}</p>
            {partnerId && (
              <Link to={`/declare/${partnerId}${est ? `?e=${est}` : ""}`}>
                <Button className="mt-5 rounded-xl font-semibold" data-testid="ls-declare-empty"><Hand size={16} className="mr-1.5" /> {t("pub.declareMyLoss")}</Button>
              </Link>
            )}
            <div className="mt-6 border-t border-border pt-5 max-w-md mx-auto">
              <p className="text-sm font-semibold text-slate-700">{t("pub.notHereYet")}</p>
              <NotifyMe partnerId={partnerId} q={q} category={category} />
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="ls-grid">
            {items.map((it) => {
              const Icon = IconFor(it.category);
              return (
                <div key={it.id} className="bg-white rounded-2xl border border-border overflow-hidden flex flex-col" data-testid={`ls-item-${it.id}`}>
                  <div className="relative h-36 bg-slate-100">
                    {it.image_base64 ? (
                      <img src={it.image_base64} alt={it.description} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full grid place-items-center text-slate-300"><Icon size={40} /></div>
                    )}
                    <span className="absolute top-2 left-2 text-[11px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">{t("pub.available")}</span>
                  </div>
                  <div className="p-3 flex-1 flex flex-col">
                    <div className="flex items-center gap-1.5 text-xs text-findo-indigo font-semibold"><Icon size={13} /> {labelFor(it.category)}</div>
                    <p className="mt-1 text-sm font-medium text-slate-800 line-clamp-2 flex-1">{it.description}</p>
                    <Button size="sm" variant="outline" onClick={() => setClaimItem(it)} className="mt-2 w-full rounded-xl font-semibold border-findo-indigo text-findo-indigo hover:bg-findo-indigo/5" data-testid={`ls-claim-${it.id}`}>
                      <Hand size={14} className="mr-1.5" /> {t("pub.itsMine")}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {items && items.length > 0 && partnerId && (
          <div className="mt-8 text-center">
            <p className="text-sm text-slate-500">{t("pub.cantFindItem")}</p>
            <Link to={`/declare/${partnerId}${est ? `?e=${est}` : ""}`}>
              <Button className="mt-3 rounded-xl font-semibold" data-testid="ls-declare-btn"><Hand size={16} className="mr-1.5" /> {t("pub.declareMyLoss")}</Button>
            </Link>
            <div className="mt-5 border-t border-border pt-5 max-w-md mx-auto">
              <p className="text-sm font-semibold text-slate-700">{t("pub.orAlertEmail")}</p>
              <NotifyMe partnerId={partnerId} q={q} category={category} />
            </div>
          </div>
        )}
      </div>

      <FalseDeclarationWarning open={!!claimItem} onCancel={() => setClaimItem(null)} onContinue={continueClaim} />
    </div>
  );
}
